import React, { useState, useEffect, useRef } from 'react';
import { apiFetch, clearToken } from '../api';

// ── Design tokens ─────────────────────────────────────────────────────────────

const T = {
  bg:         '#f4ecd9',
  paper:      '#fdf6e3',
  paperHigh:  '#fffaf0',
  ink:        '#22180e',
  inkSoft:    '#6a5a3e',
  inkMute:    '#a89570',
  accent:     '#c75a2c',
  accentSoft: '#f8dcc1',
  teal:       '#4e8a90',
  tealSoft:   '#cce0e2',
  divider:    'rgba(34,24,14,0.08)',
  display:    "'Newsreader', Georgia, serif",
  body:       "'Work Sans', system-ui, sans-serif",
  radius:     14,
  radiusLg:   22,
};

const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Newsreader:ital,opsz,wght@0,6..72,300..700;1,6..72,300..700&family=Work+Sans:wght@300..700&display=swap');
  @keyframes spin { to { transform: rotate(360deg); } }
  @keyframes fadeUp { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes wfPulse { from { transform: scaleY(0.15); } to { transform: scaleY(1); } }
  * { box-sizing: border-box; }
  input::placeholder { color: rgba(34,24,14,0.35); }
  body { margin: 0; background: ${T.bg}; color: ${T.ink}; font-family: ${T.body}; -webkit-font-smoothing: antialiased; }
  button { font-family: inherit; cursor: pointer; }
`;

// ── Icons ─────────────────────────────────────────────────────────────────────

function Icon({ name, size = 20, stroke = 1.5, color = 'currentColor' }) {
  const p = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: color, strokeWidth: stroke, strokeLinecap: 'round', strokeLinejoin: 'round' };
  switch (name) {
    case 'search':    return <svg {...p}><circle cx="11" cy="11" r="6"/><path d="M20 20l-4.5-4.5"/></svg>;
    case 'play':      return <svg {...p}><path d="M6 4.5v15l13-7.5L6 4.5z" fill={color} stroke="none"/></svg>;
    case 'pause':     return <svg {...p}><rect x="6" y="4.5" width="4" height="15" rx="1" fill={color} stroke="none"/><rect x="14" y="4.5" width="4" height="15" rx="1" fill={color} stroke="none"/></svg>;
    case 'sparkle':   return <svg {...p}><path d="M12 3v6M12 15v6M3 12h6M15 12h6"/><path d="M6.5 6.5l3 3M14.5 14.5l3 3M17.5 6.5l-3 3M9.5 14.5l-3 3" opacity="0.6"/></svg>;
    case 'arrowLeft': return <svg {...p}><path d="M19 12H5M11 6l-6 6 6 6"/></svg>;
    case 'book':      return <svg {...p}><path d="M4 4.5C4 3.7 4.7 3 5.5 3H11v17H5.5C4.7 20 4 19.3 4 18.5v-14z"/><path d="M20 4.5C20 3.7 19.3 3 18.5 3H13v17h5.5c.8 0 1.5-.7 1.5-1.5v-14z"/></svg>;
    default: return null;
  }
}

// ── Tag chip ──────────────────────────────────────────────────────────────────

function TagChip({ label, type = 'topic' }) {
  const colors = {
    person: { bg: T.accentSoft, color: T.accent },
    place:  { bg: T.tealSoft,   color: T.teal   },
    topic:  { bg: 'rgba(34,24,14,0.07)', color: T.inkSoft },
  };
  const { bg, color } = colors[type] || colors.topic;
  return (
    <span style={{ background: bg, color, fontFamily: T.body, fontSize: 12, fontWeight: 500, padding: '3px 10px', borderRadius: 99 }}>
      {label}
    </span>
  );
}

// ── Weaving loader ────────────────────────────────────────────────────────────

function WeavingLoader() {
  return (
    <div style={{ textAlign: 'center', padding: '24px 0' }}>
      <div style={{ width: 32, height: 32, border: `2px solid ${T.divider}`, borderTop: `2px solid ${T.accent}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
      <p style={{ fontFamily: T.body, fontSize: 14, color: T.inkSoft, fontStyle: 'italic', margin: 0 }}>Weaving your story…</p>
    </div>
  );
}

// ── Memory Detail ─────────────────────────────────────────────────────────────

function MemoryDetail({ memory: m, onBack }) {
  const [playing, setPlaying] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioError, setAudioError] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('memories_token');
    fetch(`/api/memories/${m.id}/audio`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(res => { if (!res.ok) throw new Error(); return res.blob(); })
      .then(blob => setAudioUrl(URL.createObjectURL(blob)))
      .catch(() => setAudioError(true));
  }, [m.id]);

  const togglePlay = () => {
    if (!audioRef.current || !audioUrl) return;
    playing ? audioRef.current.pause() : audioRef.current.play();
  };

  return (
    <div style={{ animation: 'fadeUp 0.3s ease' }}>
      <button onClick={onBack} style={{ background: 'none', border: 'none', color: T.inkSoft, fontFamily: T.body, fontSize: 15, display: 'flex', alignItems: 'center', gap: 6, padding: '0 0 16px', cursor: 'pointer' }}>
        <Icon name="arrowLeft" size={18} color={T.inkSoft} /> Back
      </button>

      <div style={{ background: T.paperHigh, border: `1px solid ${T.divider}`, borderRadius: T.radius, padding: '20px 18px' }}>
        {/* Header */}
        <div style={{ marginBottom: 14 }}>
          <span style={{ fontFamily: T.body, fontSize: 11, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: T.accent }}>{m.decade}</span>
          <h1 style={{ fontFamily: T.display, fontSize: 24, fontWeight: 400, letterSpacing: -0.3, lineHeight: 1.15, color: T.ink, margin: '6px 0 8px' }}>{m.title}</h1>
          <div style={{ fontFamily: T.body, fontSize: 13, color: T.inkMute }}>
            {new Date(m.recorded_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            {m.location_name && ` · 📍 ${m.location_name}`}
          </div>
        </div>

        {/* Tags */}
        {(m.people?.length > 0 || m.locations?.length > 0 || m.topics?.length > 0) && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 16 }}>
            {m.people?.map(p => <TagChip key={p} label={p} type="person" />)}
            {m.locations?.map(l => <TagChip key={l} label={l} type="place" />)}
            {m.topics?.map(t => <TagChip key={t} label={t} type="topic" />)}
          </div>
        )}

        {/* Audio player */}
        {audioUrl && <audio ref={audioRef} src={audioUrl} onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => setPlaying(false)} style={{ display: 'none' }} />}
        <button onClick={togglePlay} disabled={!audioUrl && !audioError} style={{
          display: 'flex', alignItems: 'center', gap: 14,
          background: audioError ? '#fdf0ee' : T.paper,
          border: `1px solid ${audioError ? '#f0c4b8' : T.divider}`,
          borderRadius: T.radius, padding: '14px 18px', width: '100%',
          marginBottom: 16, cursor: 'pointer',
        }}>
          <div style={{ width: 40, height: 40, borderRadius: '50%', background: audioError ? '#f0c4b8' : T.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, opacity: audioUrl || audioError ? 1 : 0.5 }}>
            {playing ? <Icon name="pause" size={18} color={T.paper} /> : <Icon name="play" size={18} color={T.paper} />}
          </div>
          <span style={{ fontFamily: T.display, fontSize: 17, color: audioError ? T.accent : T.ink, letterSpacing: -0.1 }}>
            {audioError ? 'Audio unavailable' : !audioUrl ? 'Loading…' : playing ? 'Pause' : 'Play recording'}
          </span>
        </button>

        {/* Prompt */}
        {m.prompt_text && (
          <div style={{ marginBottom: 8 }}>
            <button
              onClick={() => setShowPrompt(p => !p)}
              style={{ background: 'none', border: 'none', color: T.inkMute, fontFamily: T.body, fontSize: 13, padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <span style={{ fontSize: 11 }}>{showPrompt ? '▾' : '▸'}</span>
              {showPrompt ? 'Hide prompt' : 'Show prompt'}
            </button>
            {showPrompt && (
              <div style={{ background: T.paper, border: `1px solid ${T.divider}`, borderRadius: T.radius, padding: '14px 16px', marginTop: 8 }}>
                <p style={{ fontFamily: T.body, fontSize: 11, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: T.inkMute, margin: '0 0 8px' }}>Prompt</p>
                <p style={{ fontFamily: T.display, fontSize: 16, fontStyle: 'italic', color: T.inkSoft, lineHeight: 1.65, margin: 0 }}>{m.prompt_text}</p>
              </div>
            )}
          </div>
        )}
        {/* Transcript */}
        {m.transcript && (
          <div style={{ borderTop: `1px solid ${T.divider}`, paddingTop: 14 }}>
            <p style={{ fontFamily: T.body, fontSize: 11, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: T.inkMute, margin: '0 0 10px' }}>Transcript</p>
            <p style={{ fontFamily: T.body, fontSize: 15, lineHeight: 1.75, color: T.ink, margin: 0, whiteSpace: 'pre-wrap' }}>{m.transcript}</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main ViewerHome ───────────────────────────────────────────────────────────

export default function ViewerHome({ user, onLogout }) {
  const [memories, setMemories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [decade, setDecade] = useState('All');
  const [selectedMemory, setSelectedMemory] = useState(null);
  const [weaving, setWeaving] = useState(false);
  const [weaveResult, setWeaveResult] = useState(null);
  const [weaveSearch, setWeaveSearch] = useState('');

  const DECADES = ['1920s','1930s','1940s','1950s','1960s','1970s','1980s','1990s','2000s','2010s','2020s'];

  useEffect(() => {
    document.body.style.background = T.bg;
    loadMemories();
    return () => { document.body.style.background = ''; };
  }, []);

  const loadMemories = async () => {
    setLoading(true);
    try {
      const data = await apiFetch('/memories');
      setMemories(data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const filtered = memories.filter(m => {
    if (decade !== 'All' && m.decade !== decade) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      return (m.title || '').toLowerCase().includes(q) ||
        (m.transcript || '').toLowerCase().includes(q) ||
        (m.summary || '').toLowerCase().includes(q) ||
        (m.people || []).some(p => p.toLowerCase().includes(q)) ||
        (m.locations || []).some(l => l.toLowerCase().includes(q));
    }
    return true;
  });

  const runWeave = async () => {
    setWeaving(true); setWeaveResult(null);
    try {
      const body = weaveSearch.trim() ? { query: weaveSearch.trim() } : {};
      const res = await apiFetch('/memories/aggregate', { method: 'POST', body: JSON.stringify(body) });
      setWeaveResult(res.narrative);
    } catch (e) { alert('Weave failed: ' + e.message); }
    setWeaving(false);
  };

  const firstName = user?.nickname || user?.name?.split(' ')[0] || 'there';

  if (selectedMemory) {
    return (
      <>
        <style>{GLOBAL_CSS}</style>
        <div style={{ minHeight: '100vh', background: T.bg, padding: '20px' }}>
          <MemoryDetail memory={selectedMemory} onBack={() => setSelectedMemory(null)} />
        </div>
      </>
    );
  }

  return (
    <>
      <style>{GLOBAL_CSS}</style>
      <div style={{ minHeight: '100vh', background: T.bg, display: 'flex', flexDirection: 'column' }}>

        {/* Top bar */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: `1px solid ${T.divider}`, background: T.paper, position: 'sticky', top: 0, zIndex: 10 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 2 }}>
            <span style={{ fontFamily: T.display, fontSize: 22, fontWeight: 400, fontStyle: 'italic', color: T.ink, letterSpacing: -0.3 }}>memories</span>
            <span style={{ fontFamily: T.display, fontSize: 22, color: T.accent }}>.</span>
          </div>
          <button onClick={onLogout} style={{ background: 'none', border: 'none', fontFamily: T.body, fontSize: 14, color: T.inkMute, cursor: 'pointer' }}>Sign out</button>
        </div>

        <div style={{ padding: '20px 20px 40px', maxWidth: 760, margin: '0 auto', width: '100%' }}>

          {/* Greeting */}
          <div style={{ marginBottom: 24 }}>
            <h1 style={{ fontFamily: T.display, fontSize: 36, fontWeight: 400, letterSpacing: -0.4, lineHeight: 1.1, color: T.ink, margin: '0 0 6px' }}>
              Hello, <em style={{ color: T.accent }}>{firstName}.</em>
            </h1>
            <p style={{ fontFamily: T.body, fontSize: 15, color: T.inkSoft, margin: 0 }}>
              Browse and listen to your family's stories.
            </p>
          </div>

          {/* Weave bar */}
          <div style={{ background: T.paperHigh, border: `1px solid ${T.divider}`, borderRadius: T.radius, padding: '14px 16px', marginBottom: 20 }}>
            <p style={{ fontFamily: T.body, fontSize: 11, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: T.accent, margin: '0 0 10px' }}>✦ Weave a narrative</p>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                value={weaveSearch}
                onChange={e => setWeaveSearch(e.target.value)}
                placeholder="About a person, place, or topic… or leave blank for all"
                style={{ flex: 1, background: T.paper, border: `1.5px solid ${T.divider}`, borderRadius: T.radiusLg, padding: '10px 14px', fontFamily: T.body, fontSize: 14, color: T.ink, outline: 'none' }}
              />
              <button onClick={runWeave} disabled={weaving} style={{ background: T.ink, border: 'none', color: T.paper, padding: '10px 16px', borderRadius: T.radiusLg, fontFamily: T.body, fontSize: 14, fontWeight: 600, flexShrink: 0 }}>
                {weaving ? '…' : 'Weave'}
              </button>
            </div>
            {weaving && <div style={{ marginTop: 12 }}><WeavingLoader /></div>}
            {weaveResult && (
              <div style={{ marginTop: 14, borderTop: `1px solid ${T.divider}`, paddingTop: 14 }}>
                <div style={{ fontFamily: T.display, fontSize: 16, color: T.ink, lineHeight: 1.75 }}>
                  {weaveResult.split('\n').filter(Boolean).map((para, i) => <p key={i} style={{ margin: '0 0 12px' }}>{para}</p>)}
                </div>
                <button onClick={() => setWeaveResult(null)} style={{ background: 'none', border: 'none', color: T.inkMute, fontFamily: T.body, fontSize: 13, cursor: 'pointer', padding: 0 }}>Dismiss</button>
              </div>
            )}
          </div>

          {/* Search + filter — sticky */}
          <div style={{ position: 'sticky', top: 53, background: T.bg, zIndex: 9, paddingBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: T.paperHigh, border: `1.5px solid ${T.divider}`, borderRadius: T.radiusLg, padding: '10px 14px', marginBottom: 8 }}>
              <Icon name="search" size={16} color={T.inkMute} />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search stories, people, places…"
                style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', fontFamily: T.body, fontSize: 15, color: T.ink }}
              />
              {search && <button onClick={() => setSearch('')} style={{ background: 'none', border: 'none', color: T.inkMute, fontSize: 16, cursor: 'pointer', padding: 0 }}>×</button>}
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {['All', ...DECADES].map(d => (
                <button key={d} onClick={() => setDecade(d)} style={{
                  background: decade === d ? T.ink : T.paperHigh,
                  color: decade === d ? T.paper : T.inkSoft,
                  border: `1px solid ${T.divider}`,
                  borderRadius: 99, padding: '4px 12px',
                  fontFamily: T.body, fontSize: 12, fontWeight: decade === d ? 600 : 400,
                  cursor: 'pointer',
                }}>
                  {d}
                </button>
              ))}
            </div>
          </div>

          {/* Memory count */}
          <p style={{ fontFamily: T.body, fontSize: 13, color: T.inkMute, margin: '4px 0 12px' }}>
            {loading ? 'Loading…' : `${filtered.length} ${filtered.length === 1 ? 'story' : 'stories'}`}
          </p>

          {/* Memory list */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <div style={{ width: 32, height: 32, border: `2px solid ${T.divider}`, borderTop: `2px solid ${T.accent}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto' }} />
              </div>
            ) : filtered.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                <p style={{ fontFamily: T.display, fontSize: 24, fontWeight: 400, color: T.inkSoft, fontStyle: 'italic', margin: '0 0 8px' }}>No stories found.</p>
                <p style={{ fontFamily: T.body, fontSize: 14, color: T.inkMute, margin: 0 }}>Try a different search or decade.</p>
              </div>
            ) : filtered.map(m => (
              <button key={m.id} onClick={() => setSelectedMemory(m)} style={{
                background: T.paperHigh, border: `1px solid ${T.divider}`,
                borderRadius: T.radius, padding: '16px 18px',
                textAlign: 'left', width: '100%', cursor: 'pointer',
                transition: 'transform 0.12s, box-shadow 0.12s',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
                  <span style={{ fontFamily: T.body, fontSize: 11, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: T.accent }}>{m.decade}</span>
                  <span style={{ fontFamily: T.body, fontSize: 12, color: T.inkMute }}>{new Date(m.recorded_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                </div>
                <div style={{ fontFamily: T.display, fontSize: 19, color: T.ink, letterSpacing: -0.2, lineHeight: 1.2, marginBottom: m.summary ? 5 : 0 }}>{m.title}</div>
                {m.location_name && <div style={{ fontFamily: T.body, fontSize: 13, color: T.inkMute, marginBottom: 5 }}>📍 {m.location_name}</div>}
                {m.summary && <div style={{ fontFamily: T.body, fontSize: 14, color: T.inkSoft, lineHeight: 1.55 }}>{m.summary}</div>}
                {(m.people?.length > 0 || m.topics?.length > 0) && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 10 }}>
                    {m.people?.slice(0, 3).map(p => <TagChip key={p} label={p} type="person" />)}
                    {m.topics?.slice(0, 2).map(t => <TagChip key={t} label={t} type="topic" />)}
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
